import { useCallback, useEffect, useMemo, useState } from 'react';
import Ipucu from './Ipucu';
import { useSearchParams } from 'react-router-dom';
import { api, formatGun } from '../lib/api';

/**
 * DÖNEM ÇUBUĞU — gösterge ve bütün raporların ortak süzgeci.
 *
 * Seçim adres çubuğundadır (`?bas=2026-10-01&bit=2026-10-10`): sayfa yenilenince
 * korunur, bağlantı olarak paylaşılabilir. Menüden başka bir rapora geçince de
 * kaybolmasın diye son seçim oturum belleğinde tutulur. Günler İstanbul
 * takvimine göredir. Karşılaştırma dönemi = aynı uzunlukta bir önceki aralık.
 */
const ANAHTAR = 'am_donem';
const GUN = /^\d{4}-\d{2}-\d{2}$/;
const yaz = (d: Date) => d.toISOString().slice(0, 10);
const oku = (s: string) => new Date(`${s}T00:00:00Z`);
const ekle = (s: string, n: number) => yaz(new Date(oku(s).getTime() + n * 864e5));
export const bugunGun = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Istanbul' });

export type Donem = { bas: string; bit: string };
type Hazir = { id: string; ad: string; aralik: (bugun: string) => Donem };

export const HAZIR: Hazir[] = [
  { id: 'bugun', ad: 'Bugün', aralik: (b) => ({ bas: b, bit: b }) },
  // Hafta pazartesi başlar.
  { id: 'hafta', ad: 'Bu Hafta', aralik: (b) => ({ bas: ekle(b, -((oku(b).getUTCDay() + 6) % 7)), bit: b }) },
  { id: 'ay', ad: 'Bu Ay', aralik: (b) => ({ bas: `${b.slice(0, 8)}01`, bit: b }) },
  { id: 'gecenay', ad: 'Geçen Ay', aralik: (b) => {
    const son = ekle(`${b.slice(0, 8)}01`, -1);
    return { bas: `${son.slice(0, 8)}01`, bit: son };
  } },
  { id: 'uc', ad: 'Son 3 Ay', aralik: (b) => ({ bas: ekle(b, -89), bit: b }) },
  { id: 'yil', ad: 'Bu Yıl', aralik: (b) => ({ bas: `${b.slice(0, 4)}-01-01`, bit: b }) },
];

/**
 * Tanımlı dönemler (yıllar) — "Bu Yıl"ın yanında çip olarak görünür; seçilince aralık o yılın TAMAMI olur.
 * Liste `/api/donem`den bir kez çekilir (sayfa başına tek istek). Dönemleri göremeyen rolde (403) çip çıkmaz.
 */
let donemYillariSozu: Promise<number[]> | null = null;
export function donemYillariYenile() { donemYillariSozu = null; }
export function useDonemYillari(): number[] {
  const [yillar, setYillar] = useState<number[]>([]);
  useEffect(() => {
    let iptal = false;
    donemYillariSozu ??= api<{ donemler: { yil: number }[] }>('/api/donem')
      .then((r) => r.donemler.map((d) => d.yil).sort((a, b) => a - b))
      .catch(() => { donemYillariSozu = null; return []; });
    void donemYillariSozu.then((y) => { if (!iptal) setYillar(y); });
    return () => { iptal = true; };
  }, []);
  return yillar;
}
export const yilAraligi = (yil: number): Donem => ({ bas: `${yil}-01-01`, bit: `${yil}-12-31` });

const gecerli = (d: Partial<Donem> | null): d is Donem =>
  Boolean(d && typeof d.bas === 'string' && typeof d.bit === 'string' && GUN.test(d.bas) && GUN.test(d.bit)
    && !Number.isNaN(oku(d.bas).getTime()) && !Number.isNaN(oku(d.bit).getTime()) && d.bas <= d.bit);

function bellekten(): Donem | null {
  try {
    const d = JSON.parse(sessionStorage.getItem(ANAHTAR) ?? 'null') as Donem | null;
    return gecerli(d) ? d : null;
  } catch { return null; }
}

/** Geçerli dönem: önce adres, sonra oturumdaki son seçim, yoksa "Bu Ay". */
export function useDonem() {
  const [sp, setSp] = useSearchParams();
  const bas = sp.get('bas'); const bit = sp.get('bit');
  const donem = useMemo<Donem>(() => {
    const adres = { bas: bas ?? undefined, bit: bit ?? undefined };
    return gecerli(adres) ? adres : bellekten() ?? HAZIR[2].aralik(bugunGun());
  }, [bas, bit]);
  const ayarla = useCallback((d: Donem) => {
    try { sessionStorage.setItem(ANAHTAR, JSON.stringify(d)); } catch { /* gizli pencere */ }
    setSp((once) => { const y = new URLSearchParams(once); y.set('bas', d.bas); y.set('bit', d.bit); return y; }, { replace: true });
  }, [setSp]);
  return { donem, ayarla, sorgu: `bas=${donem.bas}&bit=${donem.bit}` };
}

/** "1 Eki – 10 Eki 2026" */
export const donemYazi = (d: Donem) =>
  d.bas === d.bit ? formatGun(d.bas) : `${formatGun(d.bas, d.bas.slice(0, 4) !== d.bit.slice(0, 4))} – ${formatGun(d.bit)}`;

export default function DonemSecici({ donem, ayarla, not }: { donem: Donem; ayarla: (d: Donem) => void; not?: string }) {
  const [bas, setBas] = useState(donem.bas);
  const [bit, setBit] = useState(donem.bit);
  useEffect(() => { setBas(donem.bas); setBit(donem.bit); }, [donem.bas, donem.bit]);
  const bugun = bugunGun();
  const yillar = useDonemYillari();
  const hata = !GUN.test(bas) || !GUN.test(bit) ? 'İki tarihi de seçin' : bas > bit ? 'Başlangıç bitişten sonra olamaz'
    : (oku(bit).getTime() - oku(bas).getTime()) / 864e5 > 5 * 366 ? 'En çok 5 yıllık aralık seçilebilir' : null;
  const degisti = bas !== donem.bas || bit !== donem.bit;
  return (
    <form className="ccd-donem" aria-label="Dönem seçimi"
          onSubmit={(e) => { e.preventDefault(); if (!hata) ayarla({ bas, bit }); }}>
      <div className="ccd-donem-hazir" role="group" aria-label="Hazır aralıklar">
        {HAZIR.map((h) => {
          const a = h.aralik(bugun);
          const secili = a.bas === donem.bas && a.bit === donem.bit;
          return (
            <button key={h.id} type="button" className={`ccd-donem-hap${secili ? ' ccd-is-active' : ''}`} aria-pressed={secili}
                    onClick={() => ayarla(a)}>{h.ad}</button>
          );
        })}
        {/* Tanımlı dönemler: yılın tamamı. */}
        {yillar.map((y) => {
          const a = yilAraligi(y);
          const secili = a.bas === donem.bas && a.bit === donem.bit;
          return (
            <button key={y} type="button" className={`ccd-donem-hap ccd-donem-hap-yil${secili ? ' ccd-is-active' : ''}`} aria-pressed={secili}
                    title={`${y} dönemi: 1 Ocak – 31 Aralık ${y}`} onClick={() => ayarla(a)}>{y}</button>
          );
        })}
      </div>
      <div className="ccd-donem-elle">
        <input type="date" className="ccd-input ccd-donem-tarih" aria-label="Başlangıç tarihi" value={bas} max={bit || undefined} onChange={(e) => setBas(e.target.value)} />
        <span className="ccd-meta" aria-hidden="true">–</span>
        <input type="date" className="ccd-input ccd-donem-tarih" aria-label="Bitiş tarihi" value={bit} min={bas || undefined} onChange={(e) => setBit(e.target.value)} />
        <button type="submit" className={`ccd-btn ccd-btn-sm ${degisti && !hata ? 'ccd-btn-primary' : ''}`} disabled={Boolean(hata)} title={hata ?? 'Seçilen aralığı uygula'}>Uygula</button>
        <Ipucu id="donem" />
      </div>
      {(hata || not) && <div className={`ccd-donem-not${hata ? ' ccd-donem-hata' : ''}`}>{hata ?? not}</div>}
      {/* Yazdırmada düğmeler gizlenir; dönem düz yazı olarak kalır. */}
      <div className="ccd-donem-yazdir">Dönem: {donemYazi(donem)}</div>
    </form>
  );
}
